import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { currentChartTypeAtom } from 'views/state';

import CurrentFindingsChart from './CurrentFindingsChart';
import { ModalDialogData } from '../../../types';
import {
  appliedIntegrationIdsAtom,
  bpaMetricsRefreshAtom,
  bpaRefreshAtom,
  dashboardDataAtom,
  providerFiltersAtom,
  severityFiltersAtom,
  topChartRankingsAtom,
} from '../../state';
import { BPATopBenchmarkItem, BPATopControlItem } from '../../types';
import {
  BPA_DESELECTED_COLOR,
  BPA_SEVERITY_CONFIG,
  BPA_STATUS_COLORS,
} from '../config';
import { buildBPAFindingsDrillDownUrl } from '../drillDown';
import TopFindingsChart, {
  TopFindingChartRow,
} from '../TopFindings/TopFindingsChart';
import {
  BenchmarkFindingsDetails,
  ControlFindingsDetails,
} from '../TopFindings/TopFindingsDetails';

interface Props {
  setModalComponentData: (data: ModalDialogData | undefined) => void;
}

const compactLabel = (label: string, id: number) =>
  `${label.slice(0, 38)} [${id}]`;

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const data = useAtomValue(dashboardDataAtom);
  const topRankings = useAtomValue(topChartRankingsAtom);
  const chartType = useAtomValue(currentChartTypeAtom);
  const severity = useAtomValue(severityFiltersAtom);
  const provider = useAtomValue(providerFiltersAtom);
  const integrationIds = useAtomValue(appliedIntegrationIdsAtom);
  const refreshDashboard = useSetAtom(bpaRefreshAtom);
  const refreshMetrics = useSetAtom(bpaMetricsRefreshAtom);

  if (!data) return null;

  const severityLabels = data.current.by_severity.map((item) => item.label);
  const severityCounts = data.current.by_severity.map((item) => item.count);
  const severityDatasets = data.current.by_severity_series?.map((series) => ({
    label: series.label,
    provider: series.provider,
    data: series.counts,
  })) ?? [{ label: 'Findings', data: severityCounts }];
  const selectedSeverityLabels = BPA_SEVERITY_CONFIG.filter((item) =>
    severity.includes(item.value),
  ).map((item) => item.label);
  const severityColors = data.current.by_severity.map((item) => {
    const configured = BPA_SEVERITY_CONFIG.find(
      (severityItem) => severityItem.value === item.severity,
    );
    return severity.includes(item.severity || 0)
      ? configured?.color || BPA_DESELECTED_COLOR
      : BPA_DESELECTED_COLOR;
  });
  const statusLabels = data.current.by_effective_status.map(
    (item) => item.label,
  );
  const statusCounts = data.current.by_effective_status.map(
    (item) => item.count,
  );
  const statusDatasets = data.current.by_effective_status_series?.map(
    (series) => ({
      label: series.label,
      provider: series.provider,
      data: series.counts,
    }),
  ) ?? [{ label: 'Findings', data: statusCounts }];
  const controlProviderScope = provider.length === 1 ? provider : [];
  const commonScope = { severity, provider, integrationIds };
  const getControlUrl = (item: BPATopControlItem) =>
    buildBPAFindingsDrillDownUrl({
      ...commonScope,
      controlId: item.control_pk,
      effectiveStatus: 'Active',
    });
  const getBenchmarkUrl = (item: BPATopBenchmarkItem) =>
    buildBPAFindingsDrillDownUrl({
      ...commonScope,
      benchmark: item.benchmark,
      effectiveStatus: 'Active',
    });
  const controlChartRows: TopFindingChartRow[] = topRankings.controls.map(
    (item) => ({
      key: item.control_pk.toString(),
      label: compactLabel(item.description, item.control_pk),
      activeCount: item.active_count,
      drillDownUrl: getControlUrl(item),
    }),
  );
  const benchmarkChartRows: TopFindingChartRow[] = topRankings.benchmarks.map(
    (item) => ({
      key: item.benchmark_id.toString(),
      label: compactLabel(item.description, item.benchmark_id),
      activeCount: item.active_count,
      drillDownUrl: getBenchmarkUrl(item),
    }),
  );
  const handleControlIgnoreComplete = () => {
    refreshDashboard((value) => value + 1);
    refreshMetrics((value) => value + 1);
    setModalComponentData(undefined);
  };

  return (
    <>
      <div className="col-md-6">
        <CurrentFindingsChart
          chartType={chartType}
          title="By Severity"
          labels={severityLabels}
          datasets={severityDatasets}
          includedLabels={selectedSeverityLabels}
          labelColors={severityColors}
          getDrillDownUrl={(label, providerOverride) => {
            const item = data.current.by_severity.find(
              (severityItem) => severityItem.label === label,
            );
            return item?.severity
              ? buildBPAFindingsDrillDownUrl({
                  ...commonScope,
                  severityOverride: item.severity,
                  providerOverride,
                  effectiveStatus: 'Active',
                })
              : '';
          }}
        />
      </div>
      <div className="col-md-6">
        <CurrentFindingsChart
          chartType={chartType}
          title="By Status"
          labels={statusLabels}
          datasets={statusDatasets}
          includedLabels={statusLabels}
          labelColors={statusLabels.map((label) => BPA_STATUS_COLORS[label])}
          getDrillDownUrl={(label, providerOverride) =>
            buildBPAFindingsDrillDownUrl({
              ...commonScope,
              providerOverride,
              effectiveStatus: label,
            })
          }
        />
      </div>
      <div className="col-md-6">
        <TopFindingsChart
          id="bpa_top_controls"
          chartType={chartType}
          title="Group By Controls"
          rows={controlChartRows}
          onViewDetails={() =>
            setModalComponentData({
              title: `Group By Controls (${topRankings.controls.length})`,
              bodyComponent: (
                <ControlFindingsDetails
                  rows={topRankings.controls}
                  getDrillDownUrl={getControlUrl}
                  providerCodes={controlProviderScope}
                  integrationIds={integrationIds}
                  onIgnoreComplete={handleControlIgnoreComplete}
                />
              ),
            })
          }
        />
      </div>
      <div className="col-md-6">
        <TopFindingsChart
          id="bpa_top_benchmarks"
          chartType={chartType}
          title="Group By Benchmarks"
          rows={benchmarkChartRows}
          onViewDetails={() =>
            setModalComponentData({
              title: `Group By Benchmarks (${topRankings.benchmarks.length})`,
              bodyComponent: (
                <BenchmarkFindingsDetails
                  rows={topRankings.benchmarks}
                  getDrillDownUrl={getBenchmarkUrl}
                />
              ),
            })
          }
        />
      </div>
    </>
  );
};

export default CurrentCharts;
